import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DateTime } from 'luxon';
import { NGXLogger } from 'ngx-logger';
import { EMPTY } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { CollectorListItem } from '../domain/collector-profile.model';
import { AvailabilityReminderDialogComponent } from '../my-profile/availability-reminder-dialog/availability-reminder-dialog.component';
import { CollectorAvailabilityDay } from '../my-profile/availability/availability.model';
import { nextNDays } from '../my-profile/availability/day-timeline.utils';
import { AVAILABILITY_REMINDER_DAYS_TO_CHECK } from './availability-reminder.constants';
import { MyProfileService } from './my-profile.service';

type IsoWeekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;
type AvailabilityReminderCollectorRole = 'Collector' | 'Operator';

const REMINDER_WEEKDAYS: ReadonlySet<number> = new Set<IsoWeekday>([
  2, 3, 6, 7
]);
const ELIGIBLE_COLLECTOR_ROLES: ReadonlySet<CollectorListItem['role']> =
  new Set<AvailabilityReminderCollectorRole>(['Collector', 'Operator']);
const AVAILABILITY_REMINDER_ERROR_MESSAGE =
  'Availability reminder check failed; continuing without reminder';

export function shouldCheckAvailabilityReminder(now: DateTime): boolean {
  return REMINDER_WEEKDAYS.has(now.weekday);
}

export function nextAvailabilityReminderDates(now: DateTime): string[] {
  const tomorrow = now.startOf('day').plus({ days: 1 }).toFormat('yyyy-MM-dd');
  return nextNDays(AVAILABILITY_REMINDER_DAYS_TO_CHECK, tomorrow);
}

export function hasCompleteAvailability(
  dates: string[],
  availability: CollectorAvailabilityDay[]
): boolean {
  const availabilityByDate = new Map(
    (availability ?? []).map((day) => [day.date, day])
  );

  return dates.every((date) => {
    const day = availabilityByDate.get(date);
    return !!day && (day.dayOff === true || (day.segments ?? []).length > 0);
  });
}

@Injectable({ providedIn: 'root' })
export class AvailabilityReminderService {
  private initialized = false;

  constructor(
    private authService: AuthService,
    private myProfileService: MyProfileService,
    private dialog: MatDialog,
    private logger: NGXLogger
  ) {}

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.authService
      .isAuthenticated()
      .pipe(
        filter((authenticated) => authenticated),
        take(1),
        switchMap(() => this.checkAvailability(DateTime.local())),
        catchError((error) => {
          if (error?.status !== 404) {
            this.logger.warn(AVAILABILITY_REMINDER_ERROR_MESSAGE, error);
          }
          return EMPTY;
        })
      )
      .subscribe();
  }

  private checkAvailability(now: DateTime) {
    if (!shouldCheckAvailabilityReminder(now)) {
      return EMPTY;
    }

    const dates = nextAvailabilityReminderDates(now);
    return this.myProfileService.getMyCollector().pipe(
      filter((collector) => ELIGIBLE_COLLECTOR_ROLES.has(collector.role)),
      switchMap(() =>
        this.myProfileService.getAvailability(dates[0], dates[dates.length - 1])
      ),
      tap((availability) => {
        if (!hasCompleteAvailability(dates, availability)) {
          this.dialog.open(AvailabilityReminderDialogComponent, {
            autoFocus: false,
            maxWidth: 'calc(100vw - 32px)',
            width: '480px'
          });
        }
      })
    );
  }
}
